/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */

import React from 'react';
import pt from 'prop-types';
import cx from 'classnames';
import { Field } from 'components/form';

import './overlay.scss';
import Icon from 'components/icon';
import { FadeIn } from 'components/transitions';

const DEFAULT_OFFSET_X = 10;
const DEFAULT_OFFSET_Y = 0;
const DEFAULT_VERTICAL_ALIGNMENT_OFFSET = 5;

export default class Overlay extends React.PureComponent {
  static propTypes = {
    domRect: pt.instanceOf(DOMRect).isRequired,
    items: pt.array.isRequired,
    renderItem: pt.func.isRequired,
    onClose: pt.func.isRequired,
    className: pt.string,
    offsetX: pt.number,
    offsetY: pt.number,
    search: pt.bool,
  };

  constructor(props) {
    super(props);

    this.state = {
      position: null,
      loading: true,
      search: null,
    };

    this.overlayRef = React.createRef();
  }

  componentDidMount() {
    this.calculatePosition();
  }

  /**
   * Calculates the left position of the overlay.
   *
   * By default, positions the overlay to the right of the trigger.
   * If there is not enough space on the right, it places the overlay below/above the trigger.
   * The shouldAlignVertically value means that the overlay should be positioned below/above the trigger.
   *
   * @returns {{ value: number, shouldAlignVertically: boolean }}
   */
  calculateLeftPosition() {
    const { domRect, offsetX } = this.props;
    const { width: containerWidth } =
      this.overlayRef.current.getBoundingClientRect();
    const { innerWidth: screenWidth } = window;
    const offset = typeof offsetX === 'number' ? offsetX : DEFAULT_OFFSET_X;

    // Show overlay to the right of trigger if there is enough space.
    if (domRect.right + offset + containerWidth < screenWidth) {
      return {
        value: domRect.right + offset,
        shouldAlignVertically: false,
      };
    }

    // Otherwise show overlay below/above trigger.

    // Align the left edge of the overlay with the left edge of the trigger.
    // If the overlay extends beyond the right edge of the screen, then move the overlay to the left.
    const containerOffset =
      domRect.left + offset + containerWidth > screenWidth
        ? screenWidth - (domRect.left + offset + containerWidth)
        : 0;

    return {
      value: domRect.left - Math.abs(containerOffset),
      shouldAlignVertically: true,
    };
  }

  /**
   *  Calculates the top position of the overlay.
   *
   * @param {boolean} shouldAlignVertically means that the overlay should be placed below/above the trigger
   * @returns {number}
   */
  calculateTopPosition(shouldAlignVertically) {
    const { domRect, offsetY } = this.props;
    const { height: containerHeight } =
      this.overlayRef.current.getBoundingClientRect();
    const { innerHeight: screenHeight } = window;
    const offset = typeof offsetY === 'number' ? offsetY : DEFAULT_OFFSET_Y;

    if (shouldAlignVertically) {
      const totalHeight =
        domRect.bottom + DEFAULT_VERTICAL_ALIGNMENT_OFFSET + containerHeight;

      return totalHeight < screenHeight
        ? domRect.bottom + DEFAULT_VERTICAL_ALIGNMENT_OFFSET
        : domRect.top - DEFAULT_VERTICAL_ALIGNMENT_OFFSET - containerHeight;
    }

    const totalHeight = domRect.top + offset + containerHeight;

    return totalHeight < screenHeight
      ? domRect.top + offset
      : domRect.bottom - offset - containerHeight;
  }

  calculatePosition() {
    const { value: left, shouldAlignVertically } = this.calculateLeftPosition();

    this.setState({
      position: {
        left,
        top: this.calculateTopPosition(shouldAlignVertically),
      },
    });
  }

  /** @param {React.MouseEvent} event */
  onClick = (event) => {
    event.stopPropagation();
  };

  /** @param {React.KeyboardEvent<HTMLInputElement>} event */
  onKeyDown = (event) => {
    switch (event.key) {
      case 'Escape': {
        if (event.currentTarget.value.length > 0) {
          event.currentTarget.value = '';
          this.setState({ search: '' });
        } else {
          this.props.onClose();
        }

        break;
      }

      default:
        break;
    }
  };

  /**
   * @param {React.ChangeEvent} event
   * @param {string} value
   */
  onSearchChange = (event, value) => {
    event.preventDefault();

    this.setState({ search: value.trim().toLocaleLowerCase() });
  };

  getItems() {
    const { items } = this.props;
    const { search } = this.state;

    if (search) {
      return items.filter((item) =>
        item.label.toLocaleLowerCase().includes(search),
      );
    }

    return items;
  }

  renderItems() {
    const { renderItem } = this.props;
    const items = this.getItems();

    return items.reduce((acc, item, index, allItems) => {
      const renderedItem = renderItem(item, index, allItems, acc);

      if (renderedItem) {
        acc.push(renderedItem);
      }

      return acc;
    }, []);
  }

  render() {
    const { className, onClose, search = true } = this.props;
    const { position } = this.state;

    return (
      <div className="overlay" onClick={onClose}>
        <div
          ref={this.overlayRef}
          className={cx('overlay-container', className)}
          style={position}
          onClick={this.onClick}
        >
          <FadeIn>
            {search && (
              <div className="overlay-search-container">
                <Field
                  type="text"
                  autoFocus={true}
                  className="overlay-search"
                  placeholder="Search content"
                  onKeyDown={this.onKeyDown}
                  onChange={this.onSearchChange}
                />
                <Icon fa="search" faType="solid" />
              </div>
            )}

            <div className="overlay-content-container">
              {this.renderItems()}
            </div>
          </FadeIn>
        </div>
      </div>
    );
  }
}
